import { flashbackSelfHost } from "@/data/flashback-selves";
import type { CharacterStay } from "@/data/rail-branches";
import { MARKER_JOIN_MS } from "../MarkerPresence/presence";
import { nearestHostId } from "../MarkerEmerge/emerge";
import { staysOf } from "../../rail/StayAt/stay";
import type { MarkerCircleOffset } from "@/lib/timeline/scene/SceneMotion/motion";

/** Slide someone who is leaving behind a person who stays. 0 seated, 1 tucked away. */
export function markerDepartureT(
  stays: readonly CharacterStay[],
  characterId: string,
  stay: CharacterStay,
  playheadMs: number,
): number {
  if (stayContinues(stays, characterId, stay)) {
    return 0;
  }
  const remaining = stay.endMs - playheadMs;
  if (remaining >= MARKER_JOIN_MS || remaining < 0) {
    return 0;
  }
  return easeOutCubic(1 - remaining / MARKER_JOIN_MS);
}

/** Someone who is still here after this stay ends, nearest the leaver's seat. */
export function pickDepartureHostId(
  stays: readonly CharacterStay[],
  characterId: string,
  stay: CharacterStay,
  seatAt: (id: string) => MarkerCircleOffset | null,
): string | null {
  const pool = departurePool(stays, characterId, stay);
  return nearestHostId(pool, seatAt(characterId), seatAt);
}

const poolByStay = new WeakMap<CharacterStay, string[]>();

/** A stay's pool never changes, and a long book asks for it constantly. */
function departurePool(
  stays: readonly CharacterStay[],
  characterId: string,
  stay: CharacterStay,
): string[] {
  const known = poolByStay.get(stay);
  if (known !== undefined) {
    return known;
  }
  const pool = findDeparturePool(stays, characterId, stay);
  poolByStay.set(stay, pool);
  return pool;
}

function findDeparturePool(
  stays: readonly CharacterStay[],
  characterId: string,
  stay: CharacterStay,
): string[] {
  const at = Math.max(stay.startMs, stay.endMs - 1);
  const original = flashbackSelfHost(stays, characterId, at);
  if (original !== null) {
    return [original];
  }
  const samePlace: string[] = [];
  const elsewhere: string[] = [];
  const seen = new Set<string>();
  for (const item of stays) {
    if (item.characterId === characterId || seen.has(item.characterId)) {
      continue;
    }
    if (item.startMs > at || item.endMs < at || !remainsAfter(stays, item.characterId, stay)) {
      continue;
    }
    seen.add(item.characterId);
    const bucket = item.rootPlaceId === stay.rootPlaceId ? samePlace : elsewhere;
    bucket.push(item.characterId);
  }
  return samePlace.length > 0 ? samePlace : elsewhere;
}

function remainsAfter(
  stays: readonly CharacterStay[],
  characterId: string,
  ending: CharacterStay,
): boolean {
  return staysOf(stays, characterId).some((item) =>
    item.startMs <= ending.endMs &&
    item.endMs > ending.endMs,
  );
}

function stayContinues(
  stays: readonly CharacterStay[],
  characterId: string,
  stay: CharacterStay,
): boolean {
  return staysOf(stays, characterId).some((item) =>
    item.startMs > stay.startMs &&
    item.startMs <= stay.endMs &&
    item.endMs > stay.endMs,
  );
}

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}
